import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./project_detail.css";
import { Clock, Calendar, Layers, ChevronRight } from "lucide-react";

const ProjectDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { project, totalStats } = location.state || {};
  const stats = totalStats || {
    formattedHours: "0 hr: 0 mins",
    formattedDays: "0 day",
  };

  if (!project) {
    return (
      <div className="project-detail-container">
        <div className="detail-card">
          <p>No project data found. Please go back and try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="project-detail-container">
      <div className="detail-header">
        <div className="header-left">
          <h2>{project.projectname}</h2>
          <p>Project Management System</p>
        </div>
        <div className="project-id-badge">ID: {project.projectid}</div>
      </div>

      <div className="detail-grid">
        <div className="left-side">
          <div className="detail-card">
            <h3>Project Overview</h3>
            <div className="description-box">
              {project.description ||
                "No description provided for this project."}
            </div>
          </div>

          <div className="detail-card professional-modules-card">
            <div className="card-header-with-icon">
              <Layers size={20} className="header-icon" />
              <h3>Assigned Modules</h3>
            </div>
            <div className="modules-compact-list">
              {project.modules?.map((group, gIdx) => {
                const firstTask = group.tasks?.[0] || {};
                return (
                  <div
                    key={gIdx}
                    className={`module-sleek-row status-${(firstTask.status || "progress").toLowerCase()}`}
                  >
                    <div className="module-content-main">
                      <div className="module-primary-info">
                        <div className="module-title-box">
                          <div className="module-status-dot"></div>
                          <h4>{group.modulename}</h4>
                        </div>
                        <div className="module-status-wrapper">
                          {firstTask.status && (
                            <span
                              className={`status-pill-modern ${firstTask.status.toLowerCase().replace(" ", "-")}`}
                            >
                              {firstTask.status}
                            </span>
                          )}
                        </div>
                      </div>

                      {group.tasks?.length > 0 && (
                        <div className="tasks-nested-list">
                          {group.tasks.map((task, idx) => (
                            <div
                              key={idx}
                              className="task-item-professional"
                              style={{
                                marginBottom:
                                  idx === group.tasks.length - 1
                                    ? "0px"
                                    : "16px",
                              }}
                            >
                              <div className="task-bullet"></div>
                              <div
                                style={{
                                  flex: 1,
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "4px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: "700",
                                      fontSize: "14px",
                                      color: "#1e293b",
                                    }}
                                  >
                                    {task.taskname || "General Task"}
                                  </span>
                                  <div style={{ display: "flex", gap: "8px" }}>
                                    {task.priority && (
                                      <span
                                        className={`status-pill-modern ${task.priority.toLowerCase()}`}
                                        style={{ fontSize: "9px" }}
                                      >
                                        {task.priority}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {task.remarks && (
                                  <div
                                    style={{
                                      fontSize: "12px",
                                      color: "#64748b",
                                      fontStyle: "italic",
                                      background: "#f1f5f9",
                                      padding: "6px 12px",
                                      borderRadius: "6px",
                                      marginTop: "4px",
                                    }}
                                  >
                                    "{task.remarks}"
                                  </div>
                                )}
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-end",
                                    marginTop: "4px",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "15px",
                                      fontSize: "11px",
                                      color: "#94a3b8",
                                      fontWeight: "600",
                                    }}
                                  >
                                    <span
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <Calendar size={12} />{" "}
                                      {new Date(
                                        task.deadlinedate,
                                      ).toLocaleDateString("en-GB")}
                                    </span>
                                    <span
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        color:
                                          task.status === "completed"
                                            ? "#10b981"
                                            : "#0ea5e9",
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: "6px",
                                          height: "6px",
                                          borderRadius: "50%",
                                          background: "currentColor",
                                        }}
                                      ></div>
                                      {task.status?.charAt(0).toUpperCase() +
                                        task.status?.slice(1)}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() =>
                                      navigate("/add-workreports", {
                                        state: {
                                          projectid: project.projectid,
                                          moduleid: group.moduleid,
                                          projecttaskid: task.projecttaskid,
                                          taskname: task.taskname,
                                        },
                                      })
                                    }
                                    style={{
                                      background: "none",
                                      border: "none",
                                      color: "#14b8a6",
                                      fontSize: "11px",
                                      fontWeight: "700",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "2px",
                                      padding: "0",
                                    }}
                                  >
                                    Add Report <ChevronRight size={12} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <p className="module-description-preview">
                        {group.description ||
                          "No specific instructions provided for this module."}
                      </p>

                      <div className="module-footer-meta">
                        <div className="meta-pill-group">
                          <div className="meta-pill-modern">
                            <Calendar size={14} />
                            <span>
                              {(() => {
                                let latest = null;
                                group.tasks?.forEach((t) => {
                                  const d = new Date(t.deadlinedate);
                                  if (!latest || d > latest) latest = d;
                                });
                                return latest
                                  ? latest.toLocaleDateString("en-GB")
                                  : "No Deadline";
                              })()}
                            </span>
                          </div>
                        </div>
                        <div className="module-action-area">
                          <button
                            className="modern-report-btn"
                            onClick={() =>
                              navigate("/add-workreports", {
                                state: {
                                  projectid: project.projectid,
                                  moduleid: group.moduleid,
                                },
                              })
                            }
                          >
                            <span>Add Report</span>
                            <ChevronRight size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="right-side">
          <div className="detail-card">
            <h3>Total Performance Stats</h3>
            <div className="stats-card">
              <div className="stat-item">
                <div className="stat-icon" style={{ color: "#10B981" }}>
                  <Calendar size={24} />
                </div>
                <div className="stat-info">
                  <label>Total Worked Days</label>
                  <p>{stats.formattedDays}</p>
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-icon" style={{ color: "#eb006a" }}>
                  <Clock size={24} />
                </div>
                <div className="stat-info">
                  <label>Total Worked Hours</label>
                  <p>{stats.formattedHours}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
